import { z } from 'zod'

import { idSchema, isoDateSchema, moneyPaisa } from '../common'
import { animalSummarySchema } from './animal'

/**
 * A12 · `GET /dashboard` — Home (screen 05).
 *
 * "Money tied up" and "costing you per day" are the two figures a paper
 * register cannot show, and they are the reason the app exists. Counts are
 * always present; the money keys follow §4.16 and simply vanish for a Staff
 * account, which is what makes the masked tile a typed state rather than a
 * styling convention.
 */

/**
 * One line in "needs your attention".
 *
 * The server sends a **code and its parameters**, not a finished sentence:
 * every user-facing string is rendered through `t()` on the client, and Urdu
 * is coming. `amountPaisa` is separate so it disappears on its own for an
 * account that may not see it, leaving the sentence to render without a
 * figure rather than with a hole in it.
 */
export const DASHBOARD_ALERT_CODES = [
  /** A buyer still owes on a sale — with how long it has been outstanding. */
  'RECEIVABLE_OVERDUE',
  /** This month's wanda is not in yet, so feed is still on the estimate (BR §6). */
  'BULK_FEED_MISSING',
  /** A `VARIABLE_CONFIRM` monthly cost is waiting to be confirmed (§4.14). */
  'RECURRING_NEEDS_CONFIRM',
  /** Assumed daily rate is materially below the real one (§4.15). */
  'DAILY_RATE_UNDERSTATED',
  /** An animal is past the plan's animal limit warning threshold (§4.17). */
  'PLAN_NEAR_LIMIT',
  /** Nothing has been backed up recently (G12). */
  'BACKUP_STALE',
] as const

export const dashboardAlertSchema = z.object({
  id: idSchema,
  code: z.enum(DASHBOARD_ALERT_CODES),
  tone: z.enum(['warn', 'accent']),
  /** Values the sentence interpolates — a buyer's name, a count of days. */
  params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
  amountPaisa: moneyPaisa(),
  animalId: idSchema.nullish(),
  saleId: idSchema.nullish(),
})

export const dashboardResponse = z.object({
  /** The server's day (§4.21). Every "days ago" on the screen counts from here. */
  asOf: isoDateSchema,
  /**
   * The first day anything was written in the book: the earliest purchase or
   * expense. Null for an empty book. The Reports month stepper stops here, so
   * a trader cannot step back into months that can only ever be empty.
   *
   * On this payload because `/dashboard` reaches every role and is not behind
   * the price lock — a date is not money — so the Staff report and a locked
   * owner's report can both read it. Every report route is gated one way or
   * the other.
   */
  bookStartsOn: isoDateSchema.nullable(),
  inStock: z.number().int().nonnegative(),
  soldThisSeason: z.number().int().nonnegative(),
  /** Cost basis of every held animal — capital standing in the shed. */
  moneyTiedUpPaisa: moneyPaisa(),
  /** What the whole herd costs to hold for one more day. */
  dailyBurnPaisa: moneyPaisa(),
  profitBookedPaisa: moneyPaisa(),
  /** Days held across the sales behind `profitBookedPaisa` — the tile's sub-line. */
  profitBookedDaysHeld: z.number().int().nonnegative(),
  receivableTotalPaisa: moneyPaisa(),
  /**
   * The herd's running cost per animal per day, and this month's feed split
   * into the estimate and the real bills (BR §6).
   *
   * Not on the mobile Home tiles, but the Owner and Platform consoles both
   * render a one-page summary of a book that names them, and inventing a
   * second "book summary" endpoint to carry three figures would give the two
   * surfaces two different answers the first time one of them changed.
   */
  perAnimalPerDayPaisa: moneyPaisa(),
  feedEstimatePaisa: moneyPaisa(),
  feedActualPaisa: moneyPaisa(),
  alerts: z.array(dashboardAlertSchema),
  /** "Held longest" — the two cards at the foot of Home. */
  heldLongest: z.array(animalSummarySchema),
})

export type DashboardAlert = z.infer<typeof dashboardAlertSchema>
export type DashboardResponse = z.infer<typeof dashboardResponse>
